import { Injectable } from '@angular/core';
import { Action, Store } from '@ngrx/store';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs/Observable';
import {
  ADD_NUMBERS_TO_BLACKLIST,
  ADD_NUMBERS_TO_BLACKLIST_SUCCESS,
  AddNumbersToBlacklistAction,
  AddNumbersToBlacklistFailureAction,
  AddNumbersToBlacklistSuccessAction,
  ClearInfoInBlacklistAction,
  EXPAND_MSISDN,
  ExpandMsisdnAction,
  GET_BLACKLIST,
  GET_INFO_IN_BLACKLIST,
  GetBlacklistAction,
  GetBlacklistFailureAction,
  GetBlacklistSuccessAction,
  GetInfoInBlacklistAction,
  GetInfoInBlacklistFailureAction,
  GetInfoInBlacklistSuccessAction,
  IMPORT_FILE_TO_BLACKLIST,
  IMPORT_FILE_TO_BLACKLIST_FAILURE,
  IMPORT_FILE_TO_BLACKLIST_SUCCESS,
  ImportFileToBlacklistAction,
  ImportFileToBlacklistFailureAction,
  ImportFileToBlacklistSuccessAction,
  REMOVE_NUMBERS_TO_BLACKLIST,
  REMOVE_NUMBERS_TO_BLACKLIST_SUCCESS,
  RemoveNumbersToBlacklistAction,
  RemoveNumbersToBlacklistFailureAction,
  RemoveNumbersToBlacklistSuccessAction
} from './blacklist.actions';
import { BlacklistService } from './blacklist.service';
import { of } from 'rxjs/observable/of';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { IState } from '../../shared/models/state';
import { getBlacklistParamsSelector } from './blacklist.reducer';
import { NotificationService } from '../../shared/notification.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { isValidPhoneNumber } from '../../shared/utils/validate-number';
import { checkImportFileFormat } from '../../shared/utils/file-utils';

@Injectable()
export class BlacklistEffects {
  @Effect()
  getBlacklist$: Observable<Action> = this.actions$.pipe(
    ofType(GET_BLACKLIST),
    map((action: GetBlacklistAction) => action.payload),
    switchMap(payload =>
      this.blacklistService
        .blacklist(payload)
        .pipe(
          map(response => new GetBlacklistSuccessAction(response)),
          catchError(error => of(new GetBlacklistFailureAction(error)))
        )
    )
  );

  @Effect()
  expandMsisdn$: Observable<Action> = this.actions$.pipe(
    ofType(EXPAND_MSISDN),
    map(
      (payload: ExpandMsisdnAction) =>
        payload.payload.expanded
          ? new GetInfoInBlacklistAction(payload.payload)
          : new ClearInfoInBlacklistAction(payload.payload)
    )
  );

  @Effect()
  getInfoInBlacklist$: Observable<Action> = this.actions$.pipe(
    ofType(GET_INFO_IN_BLACKLIST),
    switchMap((payload: GetInfoInBlacklistAction) =>
      this.blacklistService
        .info(payload.payload.element.msisdn)
        .pipe(
          map(response => new GetInfoInBlacklistSuccessAction(response)),
          catchError(error => of(new GetInfoInBlacklistFailureAction(error)))
        )
    )
  );

  @Effect()
  addNumbersToBlacklist$: Observable<Action> = this.actions$.pipe(
    ofType(ADD_NUMBERS_TO_BLACKLIST),
    filter(this.filterNumber('BLACKLIST.ADD_CONTACTS.INVALID_NUMBERS')),
    switchMap((payload: AddNumbersToBlacklistAction) =>
      this.blacklistService
        .addNumbersToBlackList(payload.payload)
        .pipe(
          map(response => new AddNumbersToBlacklistSuccessAction(response)),
          catchError(error => of(new AddNumbersToBlacklistFailureAction(error)))
        )
    )
  );

  @Effect()
  addNumbersToBlacklistSuccess$: Observable<Action> = this.actions$.pipe(
    ofType(ADD_NUMBERS_TO_BLACKLIST_SUCCESS),
    tap((response: any) => {
      const count = response.payload.length;
      this.notificationService.open(count === 1 ? 'BLACKLIST.SUCCESS_ADD_NUMBER' : 'BLACKLIST.SUCCESS_ADD_NUMBERS', {
        count
      });
    }),
    switchMap(() => this.permissionsService.hasPermission('BLACKLIST_PERMISSION')),
    filter(permission => permission),
    withLatestFrom(this.store.select(getBlacklistParamsSelector)),
    map(([payload, params]) => new GetBlacklistAction(params))
  );

  @Effect()
  removeNumbersToBlacklist$: Observable<Action> = this.actions$.pipe(
    ofType(REMOVE_NUMBERS_TO_BLACKLIST),
    filter(this.filterNumber('BLACKLIST.REMOVE_CONTACTS.INVALID_NUMBERS')),
    switchMap((payload: RemoveNumbersToBlacklistAction) =>
      this.blacklistService
        .removeNumbersToBlackList(payload.payload)
        .pipe(
          map(response => new RemoveNumbersToBlacklistSuccessAction(response)),
          catchError(error => of(new RemoveNumbersToBlacklistFailureAction(error)))
        )
    )
  );

  @Effect()
  removeNumbersToBlacklistSuccess$: Observable<Action> = this.actions$.pipe(
    ofType(REMOVE_NUMBERS_TO_BLACKLIST_SUCCESS),
    tap((response: any) => {
      const count = response.payload.length;
      this.notificationService.open(
        count === 1 ? 'BLACKLIST.SUCCESS_REMOVE_NUMBER' : 'BLACKLIST.SUCCESS_REMOVE_NUMBERS',
        { count }
      );
    }),
    withLatestFrom(this.store.select(getBlacklistParamsSelector)),
    map(([payload, params]) => new GetBlacklistAction(params))
  );

  @Effect()
  importFileToBlacklist$: Observable<Action> = this.actions$.pipe(
    ofType(IMPORT_FILE_TO_BLACKLIST),
    filter(this.filterImportFile('IMPORTS.WRONG_FILE_FORMAT')),
    switchMap((payload: ImportFileToBlacklistAction) =>
      this.blacklistService.importFileToBlacklist(payload.payload).pipe(
        map(
          response =>
            new ImportFileToBlacklistSuccessAction({
              import: payload.payload,
              response: response
            })
        ),
        catchError(error => of(new ImportFileToBlacklistFailureAction(error)))
      )
    )
  );

  @Effect({ dispatch: false })
  importFileToBlacklistSuccess$: Observable<Action> = this.actions$.pipe(
    ofType(IMPORT_FILE_TO_BLACKLIST_SUCCESS),
    tap((payload: any) => {
      const count = payload.payload.response.numbersCount - payload.payload.response.rejectedNumbersCount;
      this.notificationService.open(
        count === 1
          ? `BLACKLIST.SUCCESS_${payload.payload.import.action}_NUMBER`
          : `BLACKLIST.SUCCESS_${payload.payload.import.action}_NUMBERS`,
        { count }
      );
    })
  );

  @Effect({ dispatch: false })
  importFileToBlacklistFailure$: Observable<Action> = this.actions$.pipe(
    ofType(IMPORT_FILE_TO_BLACKLIST_FAILURE),
    tap((err: any) => {
      const error = err.payload.error;
      if (error instanceof Array) {
        this.notificationService.open(error[0], {}, this.notificationService.getDefaultCloseAction());
      } else {
        this.notificationService.open('IMPORTS.WRONG_FORMAT', {}, this.notificationService.getDefaultCloseAction());
      }
    })
  );

  constructor(
    private actions$: Actions,
    private blacklistService: BlacklistService,
    private notificationService: NotificationService,
    private permissionsService: NgxPermissionsService,
    private store: Store<IState>
  ) {}

  filterNumber(errorMessage: string) {
    return (payload: any): boolean => {
      const isPhoneNumbersValid = payload.payload.every(number => isValidPhoneNumber(number));
      if (!isPhoneNumbersValid) {
        this.notificationService.open(
          errorMessage,
          {
            numbers: payload.payload.filter(number => !isValidPhoneNumber(number)).join(', ')
          },
          this.notificationService.getDefaultCloseAction()
        );
      }
      return isPhoneNumbersValid;
    };
  }

  filterImportFile(errorMessage: string) {
    return (payload: any): boolean => {
      const fileName = (payload.payload.fileForm.get('file') as File).name;
      if (checkImportFileFormat(fileName)) {
        return true;
      } else {
        this.notificationService.open(errorMessage, {}, this.notificationService.getDefaultCloseAction());
        return false;
      }
    };
  }
}
